import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_SERVER_URL } from '@/constants/Config';
import { getFeatureFlag } from '@/utilities/FeatureFlags';

const baseUrl = API_SERVER_URL;

interface TSimilarProductsRes {
  seeMoreURL: string;
  similarProducts: Array<{
    id: number;
    styleCode: string;
    brand: string;
    slug: string;
    averageRating: number;
    isTrending: boolean;
    isCheaper: boolean;
    isMostFavorited: boolean;
    isTopRated: boolean;
    productShopifyId?: string;
    color: {
      name: string;
      hex_code: string;
      image_1?: {
        image: string;
      };
    };
  }>;
}

interface Response<T> {
  status: string;
  data: T;
}

enum Status {
  success = 'success',
}

export interface TSimilarProduct {
  id: number;
  imageUrl: string;
  rating: number;
  isTrending: boolean;
  isCheaper: boolean;
  isMostFavorite: boolean;
  isTopRated: boolean;
  redirectionUrl: string;
  styleCode: string;
  color: string;
  brand: string;
}

export interface TSimilarProductsResponse {
  similarProducts: TSimilarProduct[];
  seeMoreURL: string;
}

export const similarProductApi = createApi({
  reducerPath: 'similarProductApi',
  baseQuery: fetchBaseQuery({
    baseUrl,
    credentials: 'include',
  }),
  endpoints: (builder) => ({
    getSimilarProducts: builder.query<
      TSimilarProductsResponse,
      { styleCode: string; hexCode: string; forceMode?: boolean }
    >({
      serializeQueryArgs: ({ queryArgs }) => {
        const { styleCode, hexCode } = queryArgs;
        return `${styleCode}-${hexCode}`;
      },
      queryFn: async ({ styleCode, hexCode, forceMode }, _api, _extraOptions, fetchWithBQ) => {
        // Return empty data if stock communications are disabled
        const stocksChangesEnabled = await getFeatureFlag('STOCK_COMMS');
        if (!stocksChangesEnabled) {
          return {
            data: {
              similarProducts: [],
              seeMoreURL: '',
            } as TSimilarProductsResponse,
          };
        }

        const forceModeParam = forceMode ? `&forceMode=URL` : '';
        const result = await fetchWithBQ({
          url: `api/products/similar-products?style_code=${encodeURIComponent(
            styleCode
          )}&hex_code=${encodeURIComponent(hexCode)}&redirection_key=dt${forceModeParam}`,
          method: 'GET',
        });

        if (result.error) {
          return { error: result.error };
        }

        // Transform the response
        const response = result.data as Response<TSimilarProductsRes>;

        try {
          if (
            response?.status === Status.success &&
            Array.isArray(response?.data?.similarProducts)
          ) {
            const similarProducts = response.data.similarProducts.map(
              (product) =>
                ({
                  id: product?.id,
                  imageUrl: product?.color?.image_1?.image ?? '',
                  styleCode: product?.styleCode,
                  color: product?.color?.name,
                  brand: product?.brand,
                  rating: product?.averageRating,
                  isTrending: product?.isTrending,
                  isCheaper: product?.isCheaper,
                  isMostFavorite: product?.isMostFavorited,
                  isTopRated: product?.isTopRated,
                  redirectionUrl: `?productGallery=open&productId=${
                    product?.productShopifyId
                  }&colorCode=${encodeURIComponent(product?.color?.hex_code)}`,
                } as TSimilarProduct)
            );
            return {
              data: {
                similarProducts,
                seeMoreURL: response.data.seeMoreURL || '',
              } as TSimilarProductsResponse,
            };
          }
          console.error('getSimilarProducts: Invalid response', {
            status: response?.status,
            dataType: typeof response?.data,
          });
          return {
            data: {
              similarProducts: [],
              seeMoreURL: response?.data?.seeMoreURL || '',
            } as TSimilarProductsResponse,
          };
        } catch (error) {
          console.error('getSimilarProducts: Failed to transform response', { error });
          return {
            data: {
              similarProducts: [],
              seeMoreURL: '',
            } as TSimilarProductsResponse,
          };
        }
      },
    }),
  }),
});

export const { useGetSimilarProductsQuery } = similarProductApi;
